import React, { useState, useContext, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import LiveDataReport from './LiveDataReport';
import StatisticalReport from './StatisticalReport';
import AQILearningModal from '../../components/modals/AQILearningModal';
// import StatusMessage from '../../components/StatusMessage';
import { ChevronDown, X, ArrowLeft } from 'lucide-react';
import { ExportDataTypes } from '../../utils/helperFunctions';
import { DevicesContext } from '../../contexts/DevicesContext';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import StatusMessage, { StatusType } from '../../components/StatusMessage';

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isAQILearningModalOpen, setIsAQILearningModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
    // Status message state
  const [statusMessage, setStatusMessage] = useState<{
    type: StatusType;
    message: string;
    isVisible: boolean;
  }>({
    type: 'success',
    message: '',
    isVisible: false,
  });
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const { exportDeviceData, deviceSensorData, exportStatisticalReportData, statisticalReportData } = devicesContext;

  // User Selected Context
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error(
      'useUserSelected must be used within a UserSelectedProvider'
    );
  }
  // const { reportsActiveTab, setReportsActiveTab } = userSelectedContext;

  // Mobile detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTabParam = searchParams.get("tab") as "live" | "statistical";
  const reportsActiveTab = activeTabParam ?? "live";

  useEffect(() => {
    if (!activeTabParam) {
      setSearchParams({ tab: reportsActiveTab }, { replace: true });
    }
  }, [])

  const toggleTab = (tab: "live" | "statistical") => {
    setSearchParams({ tab }, { replace: true });
  }

  // Helper function to show status messages
  const showStatusMessage = (type: StatusType, message: string) => {
    setStatusMessage({
      type,
      message,
      isVisible: true,
    });
  };

  // Close status message
  const closeStatusMessage = () => {
    setStatusMessage(prev => ({ ...prev, isVisible: false }));
  };

  // sync tab from URL at page load
  // useEffect(() => {
  //   const tab = searchParams.get('tab');

  //   if (tab === 'live' || tab === 'statistical') {
  //     setReportsActiveTab(tab);
  //   } else {
  //     // default
  //     setSearchParams({ tab: 'live' },{ replace: true });
  //   }
  // }, [searchParams]);

  // sync tab from URL at page load
  // useEffect(() => {
  //   const current = searchParams.get("tab");
  // if (current !== reportsActiveTab) {
  //   if(reportsActiveTab === 'live'){
  //   setSearchParams({ tab: reportsActiveTab },{ replace: true });
  //   }
  //   else{
  //     setSearchParams({ tab: reportsActiveTab },{ replace: false });
  //   }
  // }
  // }, [reportsActiveTab]);

  const handleExport = async (fileType: string) => {
    if(reportsActiveTab == 'live') {
      try {
        console.log('Exporting data:', { fileType });
        if (deviceSensorData === null || deviceSensorData.readings.length <= 0) {
          showStatusMessage('warning', 'No data available to download for the selected date range. Please try a different period.');
          setShowExportDropdown(false);
          return;
        }
        const response = await exportDeviceData('', fileType, '', '');
        if (response?.success) {
          showStatusMessage('success', response?.message || 'Report downloaded successfully');
        } else {
          const msg = (response?.message || '').toLowerCase();
          const isNoData = !msg || /no\s+data|empty|not\s+found|no\s+records|no\s+entries/.test(msg);
          if (isNoData) {
            showStatusMessage('warning', 'No data available to download for the selected date range. Please try a different period.');
          } else {
            showStatusMessage('error', response?.message || 'Reports download failed. Please try again');
          }
        }
        setShowExportDropdown(false);
      } catch (error) {
        console.error('Export failed:', error);
      }
    } else {
        try {
          console.log('Exporting data:', { fileType });
          if (statisticalReportData.length <= 0) {
            showStatusMessage('warning', 'No data available to download for the selected date range. Please try a different period.');
            setShowExportDropdown(false);
            return;
          }
          const response = await exportStatisticalReportData(fileType);
          if (response?.success) {
          showStatusMessage('success', response?.message || 'Report downloaded successfully');
        } else {
          const msg = (response?.message || '').toLowerCase();
          const isNoData = !msg || /no\s+data|empty|not\s+found|no\s+records|no\s+entries/.test(msg);
          if (isNoData) {
            showStatusMessage('warning', 'No data available to download for the selected date range. Please try a different period.');
          } else {
            showStatusMessage('error', response?.message || 'Reports download failed. Please try again');
          }
        }
          setShowExportDropdown(false);
        } catch (error) {
          console.error('Export failed:', error);
        }
    }
  };

  return (
    <DashboardLayout>
      <div className="reports-page-container">
        {/* Mobile Header */}
        {isMobile && (
          <div className="mobile-reports-header">
            <div className="share-link-mobile-header-left">
              <ArrowLeft 
                className="share-link-back-icon" 
                size={24}
                onClick={() => navigate(-1)}
              />
              <h1 className="share-link-mobile-title">Reports</h1>
            </div>
            <div className="flex gap-2">
              {/* <div
                  className="aqi-info"
                  onClick={() => setIsAQILearningModalOpen(true)}
                >
                  <img src={`${process.env.PUBLIC_URL}/DevicePageIcons/Info.svg`} alt="Info" />
                </div> */}
              <div className="export-dropdown-container">
                <button
                  // className={`action-button export-button ${showExportDropdown ? 'active' : ''}`}
                  className="action-button-export-mobile"
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/DevicePageIcons/Export.svg`}
                    alt="Info"
                  />
                  <span>Export</span>
                  {/* <ChevronDown
                      className={`w-5 h-5 ml-2 ${showExportDropdown ? 'rotate-180' : ''}`}
                    /> */}
                </button>
                {showExportDropdown && (
                  <div
                    className="mobile-modal-overlay"
                    onClick={() => setShowExportDropdown(false)}
                  >
                    <div className="mobile-modal">
                      <div className="mobile-modal-header">
                        <h3 className="mobile-modal-title">Select Device</h3>
                        <X
                          className="w-5 h-5 text-[var(--color-text)]"
                          onClick={() => setShowExportDropdown(false)}
                        />
                      </div>
                      <div className="mobile-modal-content">
                        {ExportDataTypes.filter(
                          type =>
                            type.value === 'excel' || type.value === 'csv'
                        ).map(type => (
                          <div
                            key={type.value}
                            className="mobile-modal-option"
                            onClick={() => handleExport(type.value)}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/SVGIcons/${type.label}.svg`}
                              alt={type.label}
                            />
                            <span>{type.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Tabs */}
        {isMobile && (
          <div className="view-toggle px-5">
            <button
              onClick={() => toggleTab('live')}
              className={`toggle-button ${
                reportsActiveTab === 'live' ? 'active' : ''
              }`}
            >
              Live Data
            </button>
            <button
              onClick={() => toggleTab('statistical')}
              className={`toggle-button ${
                reportsActiveTab === 'statistical' ? 'active' : ''
              }`}
            >
              Statistical Report
            </button>
          </div>
        )}

        {/* Desktop Header */}
        {!isMobile && (
          <div className="flex items-center justify-between zoomOutLevel mt-2">
            <div className="view-toggle">
              <button
                onClick={() => toggleTab('live')}
                className={`toggle-button ${
                  reportsActiveTab === 'live' ? 'active' : ''
                }`}
              >
                Live Data
              </button>
              <button
                onClick={() => toggleTab('statistical')}
                className={`toggle-button ${
                  reportsActiveTab === 'statistical' ? 'active' : ''
                }`}
              >
                Statistical Report
              </button>
            </div>

            <div className="export-dropdown-container">
              <button
                className={`action-button export-button ${showExportDropdown ? 'active' : ''}`}
                onClick={e => {
                  (e.stopPropagation(),
                    setShowExportDropdown(!showExportDropdown));
                }}
              >
                <span>Export Data</span>
                <ChevronDown
                  className={`w-5 h-5 ml-2 ${showExportDropdown ? 'rotate-180' : ''}`}
                />
              </button>
              {showExportDropdown && (
                <div className="export-dropdown live-data-export-dropdown">
                  {ExportDataTypes.filter(
                    type => type.value === 'excel' || type.value === 'csv'
                  ).map(type => (
                    <div
                      key={type.value}
                      className="export-dropdown-item"
                      onClick={() => handleExport(type.value)}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/SVGIcons/${type.label}.svg`}
                        alt={type.label}
                      />
                      <p>{type.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {reportsActiveTab === 'live' ? (
          <LiveDataReport />
        ) : (
          <StatisticalReport />
        )}
      </div>

      <AQILearningModal
        isOpen={isAQILearningModalOpen}
        onClose={() => setIsAQILearningModalOpen(false)}
      />

      {/* Status Message */}
      <StatusMessage
        type={statusMessage.type}
        message={statusMessage.message}
        isVisible={statusMessage.isVisible}
        onClose={closeStatusMessage}
        autoHideDelay={4000} // 4 seconds
      />
    </DashboardLayout>
  );
};

export default ReportsPage;
